import { Modal } from "antd";
import classNames from "classnames";
import { ReactNode, useMemo } from "react";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import styles from "./style.module.scss";

export interface BaseModalProps {
  isOpen: boolean;
  variant: Variant;
  headers?: {
    title?: string;
    titleClasses?: string;
    subTitle?: string;
    subTitleClasses?: string;
  };
  className?: string;
  children?: ReactNode;
  footer?: ReactNode | null;
  onClose: () => void;
  icon?: React.ReactNode;
  size?: ModalSize;
  afterOpenChange?: (open: boolean) => void;
}

type ModalSize = "default" | "medium" | "large";

export type Variant = "error" | "success" | "info" | "general";
const variantIcons: Record<Variant, ReactNode> = {
  error: <CloseCircleOutlined />,
  success: <CheckCircleOutlined />,
  info: <InfoCircleOutlined />,
  general: null,
};

const getWidthBySize: Record<ModalSize, string | undefined> = {
  default: undefined,
  medium: "50%",
  large: "70%",
};

const getIsCenteredBySize: Record<ModalSize, boolean> = {
  default: false,
  medium: false,
  large: true,
};

const BaseModal = ({
  headers,
  isOpen,
  variant,
  children,
  className,
  footer = null,
  onClose,
  icon,
  size = "default",
  afterOpenChange,
}: BaseModalProps) => {
  const baseModalClassnames = classNames(
    styles.baseModal,
    className,
    styles[variant],
  );

  const displayIcon = icon ? icon : variantIcons[variant];

  const titleComponent = useMemo(() => {
    if (!headers) return null;

    return (
      <div className={styles.modalTitle}>
        <div className={styles.titleIcon}>
          {displayIcon && <div data-testid="variant-icon">{displayIcon}</div>}
          <span className={headers?.titleClasses}>{headers?.title}</span>
        </div>
        <span className={classNames(styles.subTitle, headers?.subTitleClasses)}>
          {headers?.subTitle}
        </span>
      </div>
    );
  }, [headers, displayIcon]);

  return (
    <Modal
      afterOpenChange={afterOpenChange}
      width={getWidthBySize[size]}
      centered={getIsCenteredBySize[size]}
      className={baseModalClassnames}
      title={titleComponent}
      footer={footer}
      destroyOnClose
      open={isOpen}
      onCancel={onClose}
    >
      <div className={styles.modalContent}>{children}</div>
    </Modal>
  );
};

export default BaseModal;
